import { Component, ElementRef, Inject, Input, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';

import { stateDisplayNameAndApiName, StatesDisplayAndAPI } from 'app/utilities/states';
import { AchPaymentRequest, PaymentService } from '../payment.service';
import { FormConstants } from 'app/constants/form.constants';
import { AlertsService } from 'app/ui-components/alerts/alerts.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormService } from 'app/services/form.service';
import { ApplicationInfoModel } from 'app/models/application-info.model';
import { DocumentConstants } from 'app/constants/document.constants';


@Component({
  selector: 'app-payment-info',
  templateUrl: './payment-info.component.html',
  styleUrls: ['./payment-info.component.scss']
})
export class PaymentInfoComponent implements OnInit{

  @ViewChild('accountNumberInput') accountNumberInput!: ElementRef;
  @ViewChild('routingNumberInput') routingNumberInput!: ElementRef;
  @ViewChild('firstNameInput') firstNameInput!: ElementRef;
  @ViewChild('lastNameInput') lastNameInput!: ElementRef;
  @ViewChild('street1') street1Input!: ElementRef;
  @ViewChild('street2') street2Input!: ElementRef;
  @ViewChild('city') cityInput!: ElementRef;
  @ViewChild('state') stateInput!: ElementRef;
  @ViewChild('zipCode') zipCodeInput!: ElementRef;
  @ViewChild('modalErrorDiv') modalErrorDiv!: ElementRef;

  paymentInfoForm!: FormGroup;
  selectedSection: string = ''; // Holds 'wire' or 'ach' based on selection
  states: StatesDisplayAndAPI[] = stateDisplayNameAndApiName;
  private mobileDeviceSize: boolean = false;
  private sizeChangeSubscription!: Subscription;
  price: string | undefined;
  formName : string | undefined;
  formType : string | undefined;
  formEType: string = 'New';
  submitted: boolean = false;
  transId : string | undefined;
  modalView: boolean = false;
  transErrorModal: boolean = false;
  transErrorMsg: string = '';

  sysId: string | undefined;
  caseSysId: string | undefined;
  caseNumber: string | undefined;

  // Field values
  accountType!: string;
  accountNumber!: string;
  routingNumber!: string;
  accountName!: string;
  street1!: string;
  street2!: string;
  city!: string;
  state!: string;
  zipCode!: string;
  agreeTerms: boolean = false;

  // Field errors
  accountNumberError: boolean = false;
  routingNumberError: boolean = false;
  routingNumberLenghtError: boolean = false;
  accountNameBlankError: boolean = false;
  accountNameLengthError: boolean = false;
  //lastNameError: boolean = false;
  street1Error: boolean = false;
  cityError: boolean = false;
  stateError: boolean = false;
  zipCodeError: boolean = false;
  agreeTermsError: boolean = false;

  hasCheckedForFix: boolean = false;
  paymentNeedsFixingOnFormE: boolean = false;

  constructor(private responsive: BreakpointObserver,
              protected scroller: ViewportScroller,
              private paymentService: PaymentService,
              private alertService: AlertsService,
              private formService: FormService,
              @Optional() @Inject(MAT_DIALOG_DATA) public modalData:
               { formType: string;
                 sysIdVar: string;
                 caseSysIdVar: string;
                 caseNumber: string },
              @Optional() private dialogRef: MatDialogRef<PaymentInfoComponent>
              
            ) { 
              if(this.modalData) {
                let type = this.modalData.formType;
                let caseSysIdVar = this.modalData.caseSysIdVar;
                this.modalView = true;
                const appInfo = ApplicationInfoModel.parseValues({formType: type, caseSysId: caseSysIdVar});
                this.formService.retrieveCaseAndSendFormUpdateMessage(appInfo, false);
              }
            }

  ngOnInit(): void {
    this.currentForm?.get('paymentType')?.valueChanges.subscribe(val => {
      this.selectedSection = val ? val.toLowerCase() : val;
    });

    this.sizeChangeSubscription = this.responsive.observe([Breakpoints.Handset]).subscribe(result => {
          if(result.matches) {
            this.mobileDeviceSize = true;
          } else {
            this.mobileDeviceSize = false;
          }
    });
    let type: string= '';
    let sysIdVar: string = '';
    let caseSysIdVar: string = '';
    let caseNumber: string = '';
    if (this.modalData) {
       type = this.modalData.formType;
       sysIdVar = this.modalData.sysIdVar;
       caseSysIdVar = this.modalData.caseSysIdVar;
       caseNumber = this.modalData.caseNumber;
       this.modalView = true;
       const appInfo = ApplicationInfoModel.parseValues({formType: type, caseSysId: caseSysIdVar});
       this.formService.retrieveCaseAndSendFormUpdateMessage(appInfo, false);
    } else if (this.currentForm) {
       type = this.currentForm.get('formType')?.value;
       sysIdVar = this.currentForm.get('sysId')?.value;
       caseSysIdVar = this.currentForm.get('caseSysId')?.value;
       caseNumber = this.currentForm.get('caseNumber')?.value;
    }
    //console.log (`type ${type}`)
    this.formType = type;
    this.sysId = sysIdVar;
    this.caseSysId = caseSysIdVar;
    this.caseNumber = caseNumber;
    this.price = FormConstants.FORM_PRICE_MAP.get(type);
    this.formName = FormConstants.FORM_NAME_MAP.get(type);
    
    // Only try to fix if this is a saved application, not a new one.
    if(this.sysId && this.formType) {
      this.formService.callFetchAttachmentsForCase().subscribe(
        attachmentsModel => {
          if(!this.hasCheckedForFix && attachmentsModel?.hasAttachmentsForSection(DocumentConstants.PROOF_OF_PAYMENT)) {
            if(this.isFormTypeE()) {
              this.paymentNeedsFixingOnFormE = true;
            }
            this.fixAttachmentIssueData();
          }
      });
    } 

    //console.log(`sysIdVar: ${sysIdVar}`);
    //console.log(`caseNumber: ${caseNumber}`);

    this.paymentInfoForm = new FormGroup({
      accountType: new FormControl('1', [Validators.required]),
      accountNumber: new FormControl('', [Validators.required]),
      routingNumber: new FormControl('', [Validators.required, Validators.minLength(9), Validators.maxLength(9)]),
      accountName: new FormControl('', [Validators.required, Validators.maxLength(29)]),
      street1: new FormControl('', [Validators.required]),
      street2: new FormControl('', []),
      city: new FormControl('', [Validators.required]),
      state: new FormControl('', []),
      zipCode: new FormControl('', [Validators.required, Validators.pattern("^[0-9]{5}$")])
    });
    this.initializeFields();
    this.initializeValues();
  }

  ngOnDestroy(): void {
    this.sizeChangeSubscription.unsubscribe();
  }
  initializeFields(): void {
    this.accountType = this.paymentInfoForm.get('accountType')?.value
    this.accountNumber = this.paymentInfoForm.get('accountNumber')?.value;
    this.routingNumber = this.paymentInfoForm.get('routingNumber')?.value;
    this.accountName = this.paymentInfoForm.get('accountName')?.value;
    this.street1 = this.paymentInfoForm.get('street1')?.value;
    this.street2 = this.paymentInfoForm.get('street2')?.value;
    this.city = this.paymentInfoForm.get('city')?.value;
    this.state = this.paymentInfoForm.get('state')?.value;
    this.zipCode = this.paymentInfoForm.get('zipCode')?.value;
  }

  initializeValues(): void {
    if(this.paymentType) {
      this.selectedSection = this.paymentType.toLowerCase();
    }
    
    if(this.paymentStatus && this.paymentStatus == 'PaymentComplete' && this.selectedSection == 'ach') {
      this.submitted = true;
    }

    if(this.transactionId) {
      this.transId = this.transactionId;
    }
  }

  // This fixes an issue where removing a proof of payment file cleared payment
  // info on the form even if multiple files were uploaded as proof of payment.
  fixAttachmentIssueData(): void {
    if(this.hasCheckedForFix) {
      return;
    }
    // We have the proof of payment attachment, but the data on the forms that serve as flags of completion are missing.
    if(this.hasAttachmentDataIssue()) {
      this.formEType ??= 'New';
      let amountPaid = this.currentForm?.get('paymentAmount')?.value;
      if(!amountPaid) {
        amountPaid = 0.00; // We don't know the original amount, so leave as 0.00 so it can be updated manually in Service Now.
      }
      this.formService.updateWireTransferDataOnUploadProofOfPayment(amountPaid);
      this.initializeValues(); // Re-initialize the values so we can update the UI.
    }
    this.hasCheckedForFix = true;
  }
  
  private hasAttachmentDataIssue(): boolean {
    let hasProofOfPayment = this.formService.doesCurrentFormHaveAttachmentForSection(DocumentConstants.PROOF_OF_PAYMENT) ?? false;
    let isPaymentCompleted = this.formService.isPaymentCompleted() ?? false;
    let paymentType = this.currentForm.get('paymentType')?.value;
    let isWirePaymentType = paymentType === 'WIRE';

    return hasProofOfPayment && !isPaymentCompleted && !isWirePaymentType;
  }

  updateFormEPaymentAmount(formEType: string): void {
    this.formEType = formEType;
    if(this.paymentNeedsFixingOnFormE) {
      this.formService.updateWireTransferDataOnUploadProofOfPayment(this.amountForUpload);
    }
  }

  isFormTypeE() {
    return this.formType === 'FormE';
  }

  getPricesFormTypeE(type) :string | undefined{
    return FormConstants.FORM_E_PRICE_MAP.get(type);
  }

  submitPayment () {
    var isFormValid = this.paymentInfoForm.valid;
    console.log (`isFormValid ${isFormValid}`);
    if (!isFormValid || this.state.length === 0 || !this.agreeTerms) {
        this.markFieldsInError();
        this.accountNumberInput.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
    }
    this.clearFieldsError();
    this.paymentService.processAchTransaction(this.buildAchRequest())
    .subscribe({
      next: (response) => {
        let data = response.Data;
        if (data && data.status=== 'Approved') {
          this.alertService.displaySuccess("The ACH payment has been successfully submitted.");
          this.clearForm();
          this.selectedSection = '';
          this.transId = data.transactionId;
          this.submitted = true;
          this.formService.refreshCurrentCaseData();
        } else {
          // this.dialogRef?.close();
          this.transErrorModal = true;
          this.transErrorMsg = response.ErrorMessage;
          this.modalErrorDiv.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.alertService.displayError("An error occurred while processing ACH transaction. " + response.ErrorMessage);
        }
      },
      error: (err) => {
        this.alertService.displayError("An error occurred while processing ACH transaction.");
        console.log("Error Process ACH transaction - " + err);
      }
    });
  }
  public buildAchRequest(): AchPaymentRequest {
 
    let amount = this.price;
    if (this.isFormTypeE()) {
      amount = this.getPricesFormTypeE(this.formEType);
    }

    return {
          sysId: this.sysId,
          caseSysId: this.caseSysId,
          orderId: this.caseNumber,
          achAccountType: this.accountType,
          achAccountNumber: this.accountNumber,
          achRoutingNumber: this.routingNumber,
          firstName: this.accountName,
          lastName: '',
          street1: this.street1,
          street2: this.street2,
          city: this.city,
          state: this.state,
          postalCode: this.zipCode,
          company: this.accountName,
          amount: amount + '00',
          formType: this.formType,
          formEApplicationType: this.isFormTypeE() ? this.formEType : ''
    }
    
  }

  clearForm() {
    this.clearAccountNumberInputField();
    this.clearRoutingNumberInputField();
    this.clearAccountNameInputField();
    //this.clearLastNameInputField();
    this.clearStreet1InputField();
    this.clearStreet2InputField();
    this.clearCityInputField();
    this.clearStateInputField();
    this.clearZipCodeInputField();
  }
  public clearAccountNumberInputField() {
    this.accountNumber = '';
    this.paymentInfoForm.patchValue({'accountNumber': ''});
  }
 
  public updateAccountNumber(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.accountNumber = '';
      }
    }
    this.accountNumberError=false
    this.paymentInfoForm.patchValue({'accountNumber': this.accountNumber});
  }
  public cancelAccountNumberUpdate() {
    this.accountNumber = this.paymentInfoForm.get('accountNumber')?.value;
  }
  

  /* Routing number */
  public cancelRoutingNumberUpdate() {
    this.routingNumber = this.paymentInfoForm.get('routingNumber')?.value;
  }

  public clearRoutingNumberInputField() {
    this.routingNumber = '';
    this.paymentInfoForm.patchValue({'routingNumber': ''});
  }
  public updateRoutingNumber(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.routingNumber = '';
      }
    }
    this.routingNumberError=false;
    this.routingNumberLenghtError= false;
    this.paymentInfoForm.patchValue({'routingNumber': this.routingNumber});
  }

  /* Account Name */
  public cancelAccountNameUpdate() {
    this.accountName = this.paymentInfoForm.get('accountName')?.value;
  }

  public clearAccountNameInputField() {
    this.accountName = '';
    this.paymentInfoForm.patchValue({'accountName': ''});
  }
  public updateAccountName(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.accountName = '';
      }
    }
    this.accountNameBlankError=false;
    this.accountNameLengthError = !(this.accountName?.length < 30);
    this.paymentInfoForm.patchValue({'accountName': this.accountName});
  }

  /* Street1 */
  public cancelStreet1Update() {
    this.street1 = this.paymentInfoForm.get('street1')?.value;
  }

  public clearStreet1InputField() {
    this.street1 = '';
    this.paymentInfoForm.patchValue({'street1': ''});
  }
  public updateStreet1(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.street1 = '';
      }
    }
    this.street1Error=false;
    this.paymentInfoForm.patchValue({'street1': this.street1});
  }

  /* Street2 */
  public cancelStreet2Update() {
    this.street2 = this.paymentInfoForm.get('street2')?.value;
  }
 
  public clearStreet2InputField() {
    this.street2 = '';
    this.paymentInfoForm.patchValue({'street2': ''});
  }
  public updateStreet2(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.street2 = '';
      }
    }
    this.paymentInfoForm.patchValue({'street2': this.street2});
  }

  /* City */
  public cancelCityUpdate() {
    this.city = this.paymentInfoForm.get('city')?.value;
  }

  public clearCityInputField() {
    this.city = '';
    this.paymentInfoForm.patchValue({'city': ''});
  }
  public updateCity(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.city = '';
      }
    }
    this.cityError=false;
    this.paymentInfoForm.patchValue({'city': this.city});

  }

  /* State */
  public cancelStateUpdate() {
    this.state = this.paymentInfoForm.get('state')?.value;
  }
 
  public clearStateInputField() {
    this.state = '';
    this.paymentInfoForm.patchValue({'state': ''});
  }
  public updateState(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.state = '';
      }
    }
    this.stateError=false;
    this.paymentInfoForm.patchValue({'state': this.state});
  }

  /* ZipCode */
  public cancelZipCodeUpdate() {
    this.zipCode = this.paymentInfoForm.get('zipCode')?.value;
  }
  public clearZipCodeInputField() {
    this.zipCode = '';
    this.paymentInfoForm.patchValue({'zipCode': ''});
  }
  public updateZipCode(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.zipCode = '';
      }
    }
    this.zipCodeError=false;
    this.paymentInfoForm.patchValue({'zipCode': this.zipCode});
  }
  get isMobileDeviceSize() : boolean {
    return this.mobileDeviceSize || this.modalView;
  }

  get amountForUpload(): string {
    if(this.formType === 'FormE') {
      let amount = this.getPricesFormTypeE(this.formEType);
      // Shouldn't happen, but to prevent compiler errors
      if(!amount) {
        return '0.00';
      }
      this.price = amount;
    }

    // Shouldn't happen, but to prevent compiler errors
    if(!this.price) {
      return '0.00';
    }

    return this.price;
  }

  private clearFieldsError() {
    this.accountNumberError = false;
    this.routingNumberError = false;
    this.routingNumberLenghtError = false;
    this.accountNameBlankError = false;
    this.accountNameLengthError = false;
    this.street1Error = false;
    this.cityError = false;
    this.stateError = false;
    this.zipCodeError = false;
    this.agreeTermsError = false;
  }
  private markFieldsInError() {
    this.clearFieldsError();
    Object.keys(this.paymentInfoForm.controls).forEach(key => {
      const control = this.paymentInfoForm.get(key);
      if (control && control.errors) {
        const errorNames = Object.keys(control.errors);
        if (key === 'accountNumber' ) {
          this.accountNumberError = true;
        }
        if (key === 'routingNumber' ) {
          if (errorNames[0] === 'required') {
            this.routingNumberError = true;
          }
          if (errorNames[0] === 'minlength') {
            this.routingNumberLenghtError = true;
          }
        }
        if (key === 'accountName' && !(this.accountName?.length > 0)) {
          this.accountNameBlankError = true;
        } 
        if(key === 'accountName' && this.accountName?.length > 29) {
          this.accountNameLengthError = true;
        }
        if (key === 'street1' ) {
          this.street1Error = true;
        }
        if (key === 'city' ) {
          this.cityError = true;
        }
     
        if (key === 'zipCode' ) {
          this.zipCodeError = true;
        }
      } 
      if (this.state.length === 0 ) {
         this.stateError = true;
      }
    });
    if (!this.agreeTerms) {
      this.agreeTermsError = true;
    } else {
      this.agreeTermsError = false;
    }
  }
  onNoClick() {
    this.dialogRef.close();
  }
  onNoClickWithStatus() {
    this.dialogRef.close("modalSuccess");
  }
  
  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }

  get paymentType(): string | undefined {
    return this.currentForm?.get('paymentType')?.value;
  }
  
  get paymentStatus(): string | undefined {
    return this.currentForm?.get('paymentStatus')?.value;
  }

  get transactionId(): string | undefined {
    return this.currentForm?.get('transactionId')?.value;
  }

}
